import {
    Autocomplete, Box, Card, CardHeader, Grid, List, ListItem,
    ListItemButton, ListItemText, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { HttpStages } from 'src/http/workflow/httpStages'
import { EntityWithIdAndDescription, EntityWithIdAndDescriptionFields } from 'src/types'
import { GrupoEtapaPermisoDTO } from './types'
import { HttpEtapaGrupoPermiso } from './api'
import { Controller, useForm } from 'react-hook-form'

type FilterForm = { codPais: any }
type EtapaSimple = { id: number; nombre: string }


function TabPermisoPorEtapa() {
    const { paisLogos } = usePaisLogo()
    const sistemasRequestRef = useRef(0)
    const [sistemas, setSistemas] = useState<EntityWithIdAndDescription[]>([])
    const [sistemasCodPais, setSistemasCodPais] = useState<number | null>(null)
    const [selectedSistema, setSelectedSistema] = useState<EntityWithIdAndDescription | null>(null)
    const [etapas, setEtapas] = useState<EtapaSimple[]>([])
    const [selectedEtapa, setSelectedEtapa] = useState<EtapaSimple | null>(null)
    const [grupoPermisos, setGrupoPermisos] = useState<GrupoEtapaPermisoDTO[]>([])
    const [editedPermisos, setEditedPermisos] = useState<Record<number, number>>({})

    const { control, watch } = useForm<FilterForm>({ defaultValues: { codPais: null } })
    const selectedPais = watch('codPais')
    const codPais = selectedPais?.id ?? selectedPais

    const limpiarSeleccion = () => {
        setSelectedSistema(null)
        setEtapas([])
        setSelectedEtapa(null)
        setGrupoPermisos([])
        setEditedPermisos({})
    }

    useEffect(() => {
        const requestId = sistemasRequestRef.current + 1
        sistemasRequestRef.current = requestId

        setSistemas([])
        setSistemasCodPais(null)
        limpiarSeleccion()

        if (!codPais) return

        HttpCacheWorkflow.getSistemasPorSistemaPrincipal(codPais).then(data => {
            if (sistemasRequestRef.current !== requestId) return

            setSistemas(data)
            setSistemasCodPais(codPais)
        })
    }, [codPais])

    useEffect(() => {
        setEtapas([])
        setSelectedEtapa(null)
        setGrupoPermisos([])
        if (!selectedSistema) return
        HttpStages.getStages(selectedSistema.id).then((data: any[]) => {
            setEtapas(data.map(e => ({ id: e.id, nombre: e.nombre })))
        })
    }, [selectedSistema])

    useEffect(() => {
        setGrupoPermisos([])
    }, [selectedPais])

    useEffect(() => {
        if (
            !selectedEtapa ||
            !selectedSistema ||
            !codPais ||
            sistemasCodPais !== codPais
        ) { setGrupoPermisos([]); 

return }

        HttpEtapaGrupoPermiso.getGrupoPermisosPorEtapa(selectedSistema.id, selectedEtapa.id, codPais).then(data => {
            setGrupoPermisos(data)
            const permisos: Record<number, number> = {}
            data.forEach(g => { permisos[g.idGrupo] = g.permisoTipo })
            setEditedPermisos(permisos)
        })
    }, [selectedEtapa, selectedSistema, codPais, sistemasCodPais])

    const handlePermisoChange = (idGrupo: number, permiso: number) => {
        setEditedPermisos(prev => ({ ...prev, [idGrupo]: permiso }))
    }

    const handleSave = () => {
        if (!selectedSistema || !selectedEtapa) return Promise.resolve()
        const permisos = grupoPermisos.map(g => ({ idGrupo: g.idGrupo, permisoTipo: editedPermisos[g.idGrupo] ?? 0 }))
        const promise = HttpEtapaGrupoPermiso.updateGrupoPermisos(selectedSistema.id, selectedEtapa.id, permisos)
        toast.promise(promise, { success: 'Permisos actualizados', error: 'Error al guardar', loading: 'Guardando' })
        
return promise
    }

    return (
        <Card>
            <CardHeader title='Conf. por Etapa' subheader='País → Sistema → Etapa → Grupos' />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Box sx={{ minWidth: 180, flex: 1 }}>
                    <Controller control={control} name='codPais' render={({ field }) => (
                        <Autocomplete
                            options={paisLogos.map(s => ({ id: s.id, descripcion: s[EntityWithIdAndDescriptionFields.Description] }))}
                            value={field.value}
                            onChange={(_, v) => {
                                limpiarSeleccion()
                                field.onChange(v)
                            }}
                            getOptionLabel={(o: any) => o.descripcion ?? ''}
                            isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                            size='small'
                            renderInput={params => <TextField {...params} label='País *' />}
                        />
                    )} />
                </Box>
                <Box sx={{ minWidth: 220, flex: 2 }}>
                    <Autocomplete
                        disabled={!codPais || sistemasCodPais !== codPais}
                        options={sistemas}
                        value={selectedSistema}
                        onChange={(_, v) => setSelectedSistema(v)}
                        getOptionLabel={(o: EntityWithIdAndDescription) => o[EntityWithIdAndDescriptionFields.Description]}
                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                        size='small'
                        renderInput={params => <TextField {...params} label='Sistema *' />}
                    />
                </Box>
            </Box>

            {selectedSistema && codPais ? (
                <Box sx={{ mx: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Card variant='outlined' sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                                <CardHeader title='Etapas' titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} sx={{ pb: 0 }} />
                                <Box sx={{ overflow: 'auto', flex: 1 }}>
                                    <List dense>
                                        {etapas.map(etapa => (
                                            <ListItem key={etapa.id} disablePadding>
                                                <ListItemButton selected={selectedEtapa?.id === etapa.id} onClick={() => setSelectedEtapa(etapa)}>
                                                    <ListItemText primary={etapa.nombre} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                        {etapas.length === 0 && (
                                            <ListItem><ListItemText primary='No hay etapas para este sistema' sx={{ color: 'text.secondary', textAlign: 'center' }} /></ListItem>
                                        )}
                                    </List>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Card variant='outlined' sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                                <CardHeader
                                    title={selectedEtapa ? `Grupos — ${selectedEtapa.nombre}` : 'Grupos'}
                                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                                    sx={{ pb: 0 }}
                                    action={selectedEtapa ? (
                                        <Button variant='contained' color='primary' size='small' promise={handleSave}>Guardar</Button>
                                    ) : null}
                                />
                                <Box sx={{ overflow: 'auto', flex: 1 }}>
                                    {selectedEtapa ? (
                                        <List dense disablePadding>
                                            {grupoPermisos.map(grupo => (
                                                <ListItem key={grupo.idGrupo}
                                                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 0.75 }}>
                                                    <ListItemText primary={grupo.descripcion} sx={{ flex: 1, mr: 1 }} />
                                                    <ToggleButtonGroup exclusive size='small'
                                                        value={String(editedPermisos[grupo.idGrupo] ?? 0)}
                                                        onChange={(_, v) => v !== null && handlePermisoChange(grupo.idGrupo, Number(v))}
                                                        sx={{ flexShrink: 0 }}>
                                                        <ToggleButton value='0' sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>Sin permiso</ToggleButton>
                                                        <ToggleButton value='1' sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>Lectura</ToggleButton>
                                                        <ToggleButton value='2' sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>Miembro</ToggleButton>
                                                        <ToggleButton value='3' sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>Responsable</ToggleButton>
                                                    </ToggleButtonGroup>
                                                </ListItem>
                                            ))}
                                            {grupoPermisos.length === 0 && (
                                                <ListItem><ListItemText primary='No hay grupos para este sistema y país' sx={{ color: 'text.secondary', textAlign: 'center' }} /></ListItem>
                                            )}
                                        </List>
                                    ) : (
                                        <Box sx={{ p: 4, textAlign: 'center' }}><Typography color='text.secondary'>Seleccione una etapa para ver sus grupos</Typography></Box>
                                    )}
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}><Typography color='text.secondary'>Seleccione un país y sistema para comenzar</Typography></Box>
            )}
        </Card>
    )
}

export default TabPermisoPorEtapa
